import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  Briefcase, Calendar, Star, Plus, Loader2, MapPin, CheckCircle2, Clock,
  MessageCircle, ShieldCheck, Sparkles, CreditCard, Wallet, Receipt, Heart, RotateCcw, BadgeCheck,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { useServerFn } from "@tanstack/react-start";
import { listMyJobs } from "@/lib/jobs.functions";
import { confirmBookingPaid, listMyCustomerBookings } from "@/lib/bookings.functions";
import { cancelBooking } from "@/lib/safeguards.functions";
import { listMyFavorites, rebookFromBooking, type FavoriteHelper } from "@/lib/favorites.functions";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { formatGBP } from "@/lib/pricing";

export const Route = createFileRoute("/_authenticated/dashboard")({
  validateSearch: (s) =>
    z.object({
      paid: z.coerce.number().int().optional(),
      booking: z.string().uuid().optional(),
      posted: z.coerce.number().int().optional(),
    }).partial().parse(s),
  head: () => ({ meta: [{ title: "Dashboard — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: Dashboard,
});

type Job = { id: string; title: string; postcode: string; status: string; scheduled_at: string | null; urgency: string | null; created_at: string };
type Booking = {
  id: string; status: string; payment_status: string | null; total_amount: number | null;
  scheduled_start: string | null; created_at: string;
  job: { id: string; title: string; postcode: string } | null;
  helper: { id: string; full_name: string | null; avatar_url: string | null } | null;
};

function Dashboard() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const fetchJobs = useServerFn(listMyJobs);
  const fetchBookings = useServerFn(listMyCustomerBookings);
  const fetchFavorites = useServerFn(listMyFavorites);
  const rebookFn = useServerFn(rebookFromBooking);
  const confirmPaid = useServerFn(confirmBookingPaid);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [favorites, setFavorites] = useState<FavoriteHelper[]>([]);
  const [rebookingId, setRebookingId] = useState<string | null>(null);
  const paidHandledRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [j, b, f] = await Promise.all([fetchJobs(), fetchBookings(), fetchFavorites()]);
      setJobs(j.jobs as Job[]);
      setBookings(b.bookings as unknown as Booking[]);
      setFavorites(f.favorites);
    } catch { /* ignore */ }
  }, [fetchJobs, fetchBookings, fetchFavorites]);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | undefined;
    supabase.auth.getUser().then(async ({ data, error }) => {
      if (error || !data.user) { navigate({ to: "/auth", search: { redirect: "/dashboard" } }); return; }
      setUser(data.user);
      await load();
      setLoading(false);

      if (search.paid === 1 && search.booking && !paidHandledRef.current) {
        paidHandledRef.current = true;
        const bookingId = search.booking;
        (async () => {
          for (let i = 0; i < 6; i++) {
            try {
              const res = await confirmPaid({ data: { bookingId } });
              if (res.paid) {
                toast.success("Payment confirmed — your booking is locked in.", { icon: "✅" });
                const { track } = await import("@/lib/funnel-track");
                await track("booking_paid", { booking_id: bookingId, attempts: i + 1 });
                await load();
                break;
              }
            } catch { /* try again */ }
            await new Promise((r) => setTimeout(r, 1500));
          }
          navigate({ to: "/dashboard", search: {}, replace: true });
        })();
      }

      channel = supabase
        .channel(`dash:${data.user.id}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "jobs", filter: `customer_id=eq.${data.user.id}` }, () => void load())
        .on("postgres_changes", { event: "*", schema: "public", table: "bookings", filter: `customer_id=eq.${data.user.id}` }, () => void load())
        .subscribe();
    });
    return () => { if (channel) supabase.removeChannel(channel); };
  }, [navigate, load, confirmPaid, search.paid, search.booking]);

  if (loading) {
    return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const now = Date.now();
  const upcoming = bookings.filter((b) =>
    b.status !== "completed" && b.status !== "cancelled" &&
    (!b.scheduled_start || new Date(b.scheduled_start).getTime() >= now - 24*60*60*1000)
  );
  const past = bookings.filter((b) => b.status === "completed" || b.status === "cancelled");
  const totalSpent = bookings
    .filter((b) => b.payment_status === "paid")
    .reduce((s, b) => s + Number(b.total_amount ?? 0), 0);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        {(search.paid === 1 || search.posted === 1) && (
          <ConfirmationBanner kind={search.paid === 1 ? "paid" : "posted"} bookingRef={search.booking} />
        )}

        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:flex-wrap sm:items-end sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">Welcome back</p>
            <h1 className="truncate font-display text-2xl font-bold sm:text-3xl">
              {user?.user_metadata?.full_name ?? user?.email}
            </h1>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/post-job"><Plus className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Post a job</span><span className="sm:hidden">Post</span></Link>
          </Button>
        </header>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:grid-cols-4 sm:gap-4">
          <Stat icon={Calendar} label="Upcoming" value={String(upcoming.length)} />
          <Stat icon={Briefcase} label="Total jobs" value={String(jobs.length)} />
          <Stat icon={CheckCircle2} label="Completed" value={String(past.filter(b=>b.status==="completed").length)} />
          <Stat icon={Wallet} label="Spent" value={formatGBP(totalSpent)} />
        </div>

        {/* Upcoming bookings */}
        <section className="mt-8 sm:mt-10">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold sm:text-xl">Upcoming bookings</h2>
            {upcoming.length > 0 && <span className="text-xs text-muted-foreground">{upcoming.length} active</span>}
          </div>
          {upcoming.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
              No upcoming bookings. <Link to="/post-job" className="font-medium text-primary underline-offset-4 hover:underline">Post a job</Link> to get matched.
            </p>
          ) : (
            <ul className="space-y-3">
              {upcoming.map((b) => <BookingCard key={b.id} b={b} onChanged={load} />)}
            </ul>
          )}
        </section>

        {/* Your jobs */}
        <section className="mt-10">
          <h2 className="mb-3 font-display text-lg font-semibold sm:text-xl">Your jobs</h2>
          {jobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center sm:p-12">
              <h3 className="font-display text-lg font-semibold">No jobs yet</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Post your first task to get matched with vetted Helpers near you.</p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button asChild><Link to="/post-job">Post a job</Link></Button>
                <Button asChild variant="outline"><Link to="/browse">Browse Helpers</Link></Button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              {jobs.map((j) => (
                <li key={j.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-4 sm:p-5">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{j.title}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.postcode}</span>
                      {j.scheduled_at && <span className="hidden sm:inline">{new Date(j.scheduled_at).toLocaleString("en-GB")}</span>}
                      <span>Posted {new Date(j.created_at).toLocaleDateString("en-GB")}</span>
                    </p>
                  </div>
                  <StatusBadge status={j.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Refer & earn — passive growth loop */}
        <section className="mt-8 overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-5 sm:p-6">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-base font-semibold sm:text-lg">Refer a friend, both get credit</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  £15 credit for you, £10 off for them — when they complete their first booking.
                </p>
              </div>
            </div>
            <Button asChild size="sm">
              <Link to="/referrals">Get your code</Link>
            </Button>
          </div>
        </section>


        {/* Favourite Helpers — one-tap re-engage */}
        {favorites.length > 0 && (
          <section className="mt-10">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="inline-flex items-center gap-2 font-display text-lg font-semibold sm:text-xl">
                <Heart className="h-5 w-5 fill-rose-500 text-rose-500" /> Your favourite Helpers
              </h2>
              <span className="text-xs text-muted-foreground">{favorites.length} saved</span>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((f) => (
                <li key={f.id} className="rounded-2xl border border-border bg-card p-4 shadow-card">
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      {f.avatar_url ? (
                        <img src={f.avatar_url} alt="" width={48} height={48} className="h-12 w-12 rounded-full object-cover ring-2 ring-background" />
                      ) : (
                        <span className="grid h-12 w-12 place-items-center rounded-full bg-accent font-semibold text-primary">{f.display_name.slice(0,1)}</span>
                      )}
                      {f.id_verified && (
                        <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white ring-2 ring-card" aria-label="ID verified">
                          <BadgeCheck className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <Link to="/helpers/$helperId" params={{ helperId: f.id }} className="truncate font-semibold hover:underline">{f.display_name}</Link>
                        {f.rating_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                            <Star className="h-3 w-3 fill-current" /> {f.avg_rating.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">{f.headline ?? `${f.jobs_completed} jobs · ${f.city ?? "London"}`}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <FavoriteButton helperId={f.id} helperName={f.display_name} variant="pill" initialFavorited />
                    <Button asChild size="sm" className="h-8 rounded-full text-xs">
                      <Link to="/post-job" search={{ helper: f.id } as never}><Plus className="mr-1 h-3 w-3" /> Book again</Link>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Past bookings (payment history) */}
        {past.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-3 inline-flex items-center gap-2 font-display text-lg font-semibold sm:text-xl">
              <Receipt className="h-5 w-5" /> Past bookings
            </h2>
            <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
              {past.slice(0, 10).map((b) => (
                <li key={b.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-4 sm:p-5">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{b.job?.title ?? "Booking"}</p>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {b.helper?.full_name ?? "Helper"} · {b.scheduled_start ? new Date(b.scheduled_start).toLocaleDateString("en-GB") : new Date(b.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-semibold">{formatGBP(Number(b.total_amount ?? 0))}</span>
                    <StatusBadge status={b.status} />
                    <div className="mt-1 flex flex-wrap justify-end gap-1.5">
                      {b.status === "completed" && (
                        <Button asChild size="sm" variant="outline" className="h-7 rounded-full text-xs">
                          <Link to="/bookings/$id" params={{ id: b.id }}><Star className="mr-1 h-3 w-3" /> Review</Link>
                        </Button>
                      )}
                      {(b.status === "completed" || b.status === "cancelled") && b.helper?.id && (
                        <Button
                          size="sm"
                          className="h-7 rounded-full text-xs"
                          disabled={rebookingId === b.id}
                          onClick={async () => {
                            setRebookingId(b.id);
                            try {
                              const res = await rebookFn({ data: { bookingId: b.id } });
                              if (!res.helper_active) {
                                toast.message("Helper unavailable", { description: "We've posted your job to other Helpers nearby." });
                              } else {
                                toast.success(`Re-book request sent to ${b.helper?.full_name ?? "your Helper"}`);
                              }
                              await load();
                              navigate({ to: "/dashboard", search: { posted: 1 } });
                            } catch (err) {
                              toast.error(err instanceof Error ? err.message : "Could not re-book");
                            } finally { setRebookingId(null); }
                          }}
                        >
                          {rebookingId === b.id ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1 h-3 w-3" />} Re-book
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Mobile sticky CTA */}
        <div className="sticky bottom-3 z-40 mt-10 sm:hidden">
          <Button asChild size="lg" className="w-full rounded-full shadow-elegant">
            <Link to="/post-job"><Plus className="mr-1 h-4 w-4" /> Post a job</Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function BookingCard({ b, onChanged }: { b: Booking; onChanged: () => void | Promise<void> }) {
  const needsPay = b.payment_status !== "paid" && (b.status === "pending" || b.status === "accepted");
  const canCancel = ["pending", "accepted", "in_progress"].includes(b.status);
  const [cancelling, setCancelling] = useState(false);
  const cancelFn = useServerFn(cancelBooking);

  async function handleCancel() {
    const reason = window.prompt("Tell us why you're cancelling (helps us improve):");
    if (!reason || reason.trim().length < 3) return;
    setCancelling(true);
    try {
      const res = await cancelFn({ data: { bookingId: b.id, reason: reason.trim() } });
      if (res.refundAmount > 0) toast.success(`Cancelled. Refund of £${res.refundAmount.toFixed(2)} on its way.`);
      else toast.success("Cancelled.");
      await onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not cancel");
    } finally { setCancelling(false); }
  }

  return (
    <li className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="min-w-0">
          <p className="truncate font-display font-semibold">{b.job?.title ?? "Booking"}</p>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {b.helper?.full_name ?? "Awaiting Helper"} · {b.job?.postcode}
          </p>
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {b.scheduled_start && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(b.scheduled_start).toLocaleString("en-GB")}</span>}
            <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{formatGBP(Number(b.total_amount ?? 0))}</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StatusBadge status={b.status} />
          <PaymentBadge status={b.payment_status} />
        </div>
      </div>
      {needsPay && b.job?.id && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <span className="inline-flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Helper assigned — pay to lock it in.</span>
          <Button asChild size="sm" className="h-8 rounded-full">
            <Link to="/jobs/$jobId/confirm" params={{ jobId: b.job.id }}>Confirm &amp; pay</Link>
          </Button>
        </div>
      )}
      {canCancel && (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-destructive" disabled={cancelling} onClick={handleCancel}>
            {cancelling ? "Cancelling…" : "Cancel booking"}
          </Button>
        </div>
      )}
    </li>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Briefcase; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card sm:p-5">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent text-primary sm:h-10 sm:w-10"><Icon className="h-4 w-4 sm:h-5 sm:w-5" /></span>
        <div className="min-w-0">
          <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">{label}</p>
          <p className="font-display text-lg font-bold sm:text-2xl">{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: "bg-accent text-primary",
    pending: "bg-amber-100 text-amber-900",
    accepted: "bg-blue-100 text-blue-900",
    assigned: "bg-amber-100 text-amber-900",
    in_progress: "bg-blue-100 text-blue-900",
    completed: "bg-emerald-100 text-emerald-900",
    cancelled: "bg-muted text-muted-foreground",
    disputed: "bg-red-100 text-red-900",
  };
  const cls = map[status] ?? "bg-muted text-muted-foreground";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ${cls}`}>{status.replace("_", " ")}</span>;
}

function PaymentBadge({ status }: { status: string | null }) {
  if (!status) return null;
  if (status === "paid") return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Paid</span>;
  if (status === "unpaid") return <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300">Awaiting payment</span>;
  if (status === "processing") return <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300">Processing</span>;
  if (status === "refunded") return <span className="text-[10px] font-medium text-muted-foreground">Refunded</span>;
  return null;
}

function ConfirmationBanner({ kind, bookingRef }: { kind: "paid" | "posted"; bookingRef?: string }) {
  const isPaid = kind === "paid";
  const title = isPaid ? "Payment confirmed — you're all set" : "Job posted — Helpers are being matched";
  const subtitle = isPaid
    ? "Your Helper has been notified and your funds are safely held in escrow until the job is done."
    : "We're notifying the best-matched Helpers in your area right now. You'll see quotes within minutes.";
  const ref = (bookingRef ?? "").slice(0, 8).toUpperCase();
  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-5 shadow-card sm:mb-8 sm:p-7 dark:border-emerald-900/40 dark:from-emerald-950/30 dark:via-card dark:to-card">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-700 sm:h-11 sm:w-11 dark:text-emerald-300">
          {isPaid ? <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" /> : <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-semibold sm:text-2xl">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          {ref && (
            <p className="mt-2 text-xs text-muted-foreground">
              Booking reference · <span className="font-mono font-semibold text-foreground">#{ref}</span>
            </p>
          )}
        </div>
      </div>

      <ol className="mt-5 grid gap-3 sm:grid-cols-3">
        <NextStep icon={<Clock className="h-4 w-4" />} title={isPaid ? "Helper on the way" : "Quotes in < 5 min"} body={isPaid ? "They'll message you to confirm arrival time." : "Helpers typically respond within 5 minutes."} />
        <NextStep icon={<MessageCircle className="h-4 w-4" />} title="Chat in-app" body="Share access details, photos, and timing directly with your Helper." />
        <NextStep icon={<ShieldCheck className="h-4 w-4" />} title="Protected booking" body={isPaid ? "Funds held until you confirm the job is complete." : "Free to cancel any time before you pay."} />
      </ol>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline" className="rounded-xl"><Link to="/contact">Need help? Contact support</Link></Button>
        <Button asChild size="sm" variant="ghost" className="rounded-xl"><Link to="/how-it-works">How Peopels protects you</Link></Button>
      </div>
    </div>
  );
}

function NextStep({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <li className="rounded-xl border border-border bg-card p-3">
      <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <span className="text-primary">{icon}</span> {title}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{body}</p>
    </li>
  );
}
